import { useEffect, useState } from 'react';

/**
 * Guest-side on/off switch for the animated decoration (manga cursor, cursor
 * trail, onomatopoeia, falling petals, fly-to-cart). Stored per browser, not
 * per restaurant: it is a comfort setting of the person looking at the page.
 * Default is ON — turning it off is an explicit choice.
 */
const KEY = 'fxAnimations';
const EVENT = 'fx-animations-change';

export function readFxEnabled(): boolean {
  if (typeof window === 'undefined') return true;
  try { return window.localStorage.getItem(KEY) !== 'off'; } catch { return true; }
}

export function setFxEnabled(on: boolean): void {
  if (typeof window === 'undefined') return;
  try { window.localStorage.setItem(KEY, on ? 'on' : 'off'); } catch { /* private mode — keep the session value */ }
  // Same-document listeners: `storage` only fires in OTHER tabs.
  window.dispatchEvent(new CustomEvent(EVENT));
}

/** Subscribes to the switch — updates on toggle here and in another tab. */
export function useFxEnabled(): boolean {
  const [on, setOn] = useState(readFxEnabled);

  useEffect(() => {
    const sync = () => setOn(readFxEnabled());
    sync();
    window.addEventListener(EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  return on;
}
